// /app/frontend/src/components/frugal-v2/sections/ContingencyReserveSection.jsx
// Contingency Reserve card - shows %, amount, allows editing %

import React, { useState } from 'react';
import { Shield, Pencil, Check, X, ChevronDown, AlertTriangle } from 'lucide-react';
import * as api from '../services/frugalApi';

const ContingencyReserveSection = ({ zen, token, t, onUpdate, fundingStatus }) => {
  const [editing, setEditing] = useState(false);
  const [newPercent, setNewPercent] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const percent = zen?.contingency_percent || 0;
  const balance = zen?.contingency_balance || 0;
  const budget = zen?.contingency_budget || 0;
  const actualUsed = Math.max(0, budget - balance);
  // STRICT: contingency is only "being used" when Daily Zen is actually
  // exhausted AND there's a current drain. Historical records ignored.
  const isBeingUsed = zen?.contingency_being_used ?? false;

  const handleEdit = (e) => {
    e.stopPropagation();
    setNewPercent(String(percent));
    setEditing(true);
    setIsExpanded(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    const val = parseFloat(newPercent);
    if (isNaN(val) || val < 0 || val > 50) return;
    try {
      const currentBudget = await api.fetchBudget(token);
      const categories = currentBudget?.categories || [];
      await api.saveBudget(token, { categories, contingency_percent: val });
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error saving contingency %:', err);
      // Surface a friendly error so user knows save failed
      alert(t('frugal.actions.error', 'No se pudo guardar el cambio'));
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setEditing(false);
  };

  const fillPercent = budget > 0 ? Math.min(100, (balance / budget) * 100) : 0;

  // Funding status colors
  const borderClass = fundingStatus === 'funded' ? 'border-emerald-500/40'
    : fundingStatus === 'partial' ? 'border-amber-500/40'
    : 'border-white/10';
  const titleClass = fundingStatus === 'funded' ? 'text-emerald-400'
    : fundingStatus === 'partial' ? 'text-amber-400'
    : fundingStatus === 'none' ? 'text-slate-400'
    : 'text-amber-400';

  return (
    <div className={`rounded-xl border overflow-hidden bg-white/5 ${borderClass}`}>
      {/* Header - same structure as CollapsibleSection */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className={`w-5 h-5 ${titleClass}`} />
            <span className={`font-semibold ${titleClass}`}>
              {t('frugal.contingencyReserve.title', 'Reserva Contingencia')}
            </span>
            {!editing ? (
              <button onClick={handleEdit}
                className="ml-1 w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-slate-400 transition-colors"
                data-testid="contingency-edit-btn"
              >
                <Pencil className="w-3 h-3" />
              </button>
            ) : (
              <div className="flex items-center gap-1 ml-1">
                <button onClick={handleSave} className="p-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white">
                  <Check className="w-3 h-3" />
                </button>
                <button onClick={handleCancel} className="p-1 rounded-full bg-slate-700 hover:bg-slate-600 text-white">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          <button onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <span className={`font-bold text-lg ${balance <= 0 ? 'text-red-400' : 'text-yellow-400'}`}>
              ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Línea 2: subtítulo alineado a la derecha (igual que CollapsibleSection) */}
        <div className="flex justify-end mt-1">
          <span className="text-xs text-slate-400">
            {percent}% • {t('frugal.contingencyReserve.budgeted', 'Presupuestado')}: <span className="text-orange-400 font-medium">${budget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> • {t('frugal.contingencyReserve.used', 'Usado')}: <span className={`font-medium ${actualUsed > 0 ? 'text-yellow-400' : 'text-slate-300'}`}>${actualUsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </span>
        </div>

        {/* Warning when contingency is being used */}
        {isBeingUsed && (
          <div className="mt-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
            <span className="text-xs text-yellow-400 font-medium">
              {t('frugal.contingencyReserve.usingWarning')}
            </span>
          </div>
        )}
      </div>

      {/* Expanded: progress bar + details + edit */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-white/10 pt-3 space-y-3">
          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${fillPercent}%`,
                backgroundColor: fillPercent >= 100 ? '#10B981' : fillPercent > 0 ? '#F59E0B' : '#EF4444'
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500">
            <span>{Math.round(fillPercent)}% {t('frugal.contingencyReserve.funded', 'fondeado')}</span>
            {actualUsed > 0 && (
              <span>{t('frugal.contingencyReserve.used', 'Usado')}: ${actualUsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            )}
          </div>
          {editing && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-slate-400">%</span>
              <input type="number" min="0" max="50" step="1" value={newPercent}
                onChange={(e) => setNewPercent(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-16 bg-slate-700 text-white text-sm px-2 py-1 rounded text-center focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="contingency-percent-input"
                autoFocus
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContingencyReserveSection;
